import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Share2, Facebook, Twitter, Linkedin, Download, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { fetchArticleBySlug, fetchRelatedArticles } from '../services/articleService';
import { Article } from '../types/article';
import ArticleCard from '../components/articles/ArticleCard';
import LoadingIndicator from '../components/common/LoadingIndicator';
import { validateSlug } from '../utils/slugUtils';
import { ROUTES } from '../constants/routes';
import ImageWithFallback from '../components/common/ImageWithFallback';
import { useLanguage } from '../contexts/LanguageContext';
import ArticleTags from '../components/articles/ArticleTags';
import ArticleAccess from '../components/auth/ArticleAccess';

const ArticlePage: React.FC = () => {
  const { articleSlug } = useParams<{ articleSlug: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<Article | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isHindi, language, t } = useLanguage();

  useEffect(() => {
    const loadArticle = async () => {
      const validSlug = validateSlug(articleSlug);

      if (!validSlug) {
        setError('Article not found - invalid URL');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const articleData = await fetchArticleBySlug(validSlug);

        if (articleData) {
          setArticle(articleData);
          document.title = `${articleData.title} | Legal Nest`;
          window.scrollTo(0, 0);

          if (articleData.category) {
            const related = await fetchRelatedArticles(articleData.category, validSlug);
            setRelatedArticles(related);
          }
        } else {
          setError('Article not found');
        }
      } catch (err) {
        setError('Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [articleSlug, navigate]);

  const handleShare = (platform: string) => {
    if (!article) return;
    
    const url = window.location.href;
    const title = article.title;
    let shareUrl = '';
    
    switch(platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(url)
          .then(() => alert('Link copied to clipboard!'))
          .catch(err => console.error('Error copying to clipboard:', err));
        return;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <div className="container-custom py-8">
        <LoadingIndicator message="Loading article..." />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="container-custom py-12 text-center">
        <h1 className="text-3xl font-serif font-bold mb-4">Article Not Found</h1>
        <p className="text-neutral-600 mb-6">
          {error || "The article you are looking for does not exist or has been removed."}
        </p>
        <Link to="/" className="btn btn-primary">Return to Home</Link>
      </div>
    );
  }

  const renderContent = () => {
    const content = isHindi && article.content_hi ? article.content_hi : article.content;
    return (content?.split('\n\n') || []).map((p, i) => (
      <p key={i} className="mb-4 leading-relaxed text-base sm:text-lg">
        {p}
      </p>
    ));
  };

  return (
    <ArticleAccess>
      <div className="container-custom py-8">
        <Link to="/" className="inline-flex items-center text-neutral-500 hover:text-primary-500 mb-6">
          <ArrowLeft size={16} className="mr-1" /> {t('back_to_home')}
        </Link>

        <article>
          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl font-serif font-bold mb-4">
              {isHindi && article.title_hi ? article.title_hi : article.title}
            </h1>
            <div className="flex flex-wrap items-center text-sm text-neutral-600 mb-6">
              <span className="font-medium">{article.author || "Anonymous"}</span>
              <span className="mx-2">•</span>
              <time>{article.date ? format(new Date(article.date), 'MMMM d, yyyy') : "Unknown date"}</time>
            </div>

            {article.image && (
              <div className="mb-6">
                <ImageWithFallback
                  src={article.image}
                  alt={article.imageCaption || article.title}
                  className="w-full h-auto rounded-lg"
                />
                {article.imageCaption && <p className="text-sm text-neutral-500 mt-2 text-center">{article.imageCaption}</p>}
              </div>
            )}
          </header>

          <div className="prose prose-lg max-w-none">
            {renderContent()}
          </div>
          {/* Tags section */}
          <div className="mt-8">
            <ArticleTags tags={article.tags || []} />
          </div>

          {/* Share buttons moved after content */}
          <div className="mt-8 pt-6 border-t border-neutral-200">
            <h3 className="font-serif font-bold mb-4">Share this article</h3>
            <div className="flex flex-wrap gap-3">
              <button className="inline-flex items-center px-3 py-1 bg-neutral-100 hover:bg-neutral-200 rounded-full text-sm transition-colors" onClick={() => handleShare('facebook')}>
                <Facebook size={14} className="mr-1" />
                Share
              </button>
              <button className="inline-flex items-center px-3 py-1 bg-neutral-100 hover:bg-neutral-200 rounded-full text-sm transition-colors" onClick={() => handleShare('twitter')}>
                <Twitter size={14} className="mr-1" />
                Tweet
              </button>
              <button className="inline-flex items-center px-3 py-1 bg-neutral-100 hover:bg-neutral-200 rounded-full text-sm transition-colors" onClick={() => handleShare('linkedin')}>
                <Linkedin size={14} className="mr-1" />
                LinkedIn
              </button>
              <button className="inline-flex items-center px-3 py-1 bg-neutral-100 hover:bg-neutral-200 rounded-full text-sm transition-colors" onClick={() => handleShare('copy')}>
                <Share2 size={14} className="mr-1" />
                Copy Link
              </button>
            </div>
          </div>

          {article.pdf_url && (
            <div className="my-8 p-4 border border-primary-100 rounded-lg bg-primary-50">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start sm:items-center">
                  <FileText size={24} className="text-primary-600 mr-3 shrink-0 mt-1 sm:mt-0" />
                  <div>
                    <p className="font-medium text-primary-800">
                      {article.category?.includes("Court") || article.tags?.some(tag => tag.includes("Court") || tag.includes("Judgment")) 
                        ? "Download PDF of Full Judgment" 
                        : "Download PDF Version"}
                    </p>
                    <p className="text-sm text-primary-600">
                      {article.category?.includes("Supreme") ? "Supreme Court of India" : 
                      article.category?.includes("High") ? "High Court Judgment" : 
                      "Official Document"}
                    </p>
                  </div>
                </div>
                <a 
                  href={article.pdf_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn btn-primary flex items-center w-full sm:w-auto justify-center"
                >
                  <Download size={16} className="mr-2" />
                  Download PDF
                </a>
              </div>
            </div>
          )}
          
          {/* Author section */}
          <div className="mt-8 pt-6 border-t border-neutral-200">
            <div className="bg-neutral-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-primary-100 text-primary-500 rounded-full flex items-center justify-center mr-4">
                  <span className="text-xl font-serif font-bold">{(article.author || "A")[0]}</span>
                </div>
                <div>
                  <h3 className="font-serif font-bold">{article.author || "Anonymous"}</h3>
                  <p className="text-sm text-neutral-600">Legal Correspondent</p>
                </div>
              </div>
              <p className="text-neutral-700">
                {article.authorBio || `${article.author || "Our correspondent"} is a legal correspondent at Legal Nest covering the latest developments in the legal field.`}
              </p>
            </div>
          </div>
        </article>
        
        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-serif font-bold mb-6">Related Articles</h2>
            <div className="grid grid-cols-12 sm:grid-cols-12 md:grid-cols-13 lg:grid-cols-3 gap-4">
              {relatedArticles.slice(0, 4).map((relatedArticle) => (
                <div key={relatedArticle.id} className="flex flex-col h-[320px]">
                  <Link to={ROUTES.ARTICLE_BY_SLUG(relatedArticle.slug)} className="block mb-2">
                    <img 
                      src={relatedArticle.image} 
                      alt={relatedArticle.title} 
                      className="w-full h-52 object-cover rounded-lg"
                    />
                  </Link>
                  <h3 className="text-base font-medium line-clamp-2 mb-1">
                    <Link 
                      to={ROUTES.ARTICLE_BY_SLUG(relatedArticle.slug)} 
                      className="hover:text-primary-500"
                    >
                      {relatedArticle.title}
                    </Link>
                  </h3>
                  <div className="text-xs text-neutral-500 mt-auto">
                    {relatedArticle.date ? format(new Date(relatedArticle.date), 'MMM d, yyyy') : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </ArticleAccess>
  );
};

export default ArticlePage;